import { membershipService } from '@/data/membership';
import type { Membership } from '@/contracts/membership';
import type { ProPricingResult, ProductPricing } from '@/types/pricing';

/**
 * Determine the base price for a product considering sales and dynamic pricing
 */
export function getBasePrice(product: ProductPricing): number | null {
  // Check if sale is active
  if (product.sale_enabled && product.sale_price && product.sale_price > 0) {
    const now = new Date();
    const start = product.sale_starts_at ? new Date(product.sale_starts_at) : new Date(0);
    const end = product.sale_ends_at ? new Date(product.sale_ends_at) : new Date(0);
    
    if (now >= start && now <= end) {
      return product.sale_price;
    }
  }
  
  // Fall back to current_price if set
  if (product.current_price && product.current_price > 0) {
    return product.current_price;
  }
  
  // Fall back to standard price
  return product.price || null;
}

/**
 * Calculate Pro pricing for a product based on user membership
 */
export function getProPricing(product: ProductPricing, membership: Membership | null): ProPricingResult {
  const isPro = membershipService.isPro(membership);
  const retailPrice = product.msrp ?? product.price ?? null;
  const basePrice = getBasePrice(product);
  
  // Default result for non-Pro or invalid pricing
  const defaultResult: ProPricingResult = {
    mode: 'none',
    retailPrice,
    basePrice,
    proPrice: null,
    rebatePercent: null,
    rebateAmount: null,
    hasSavings: false,
    savingsAmount: null,
  };
  
  // Return early if user is not Pro or base price is invalid
  if (!isPro || !basePrice || basePrice <= 0) {
    return defaultResult;
  }
  
  const mode = product.pro_discount_mode || 'none';
  
  // No discount mode
  if (mode === 'none') {
    return defaultResult;
  }
  
  // Instant or Vendor Adjusted mode
  if (mode === 'instant' || mode === 'vendorAdjusted') {
    let percent = product.pro_instant_percent ?? 10;
    // Clamp between 0 and 50
    percent = Math.min(Math.max(percent, 0), 50);
    
    if (percent === 0) {
      return defaultResult;
    }
    
    const factor = (100 - percent) / 100;
    const proPrice = Math.round(basePrice * factor);
    const savingsAmount = basePrice - proPrice;
    
    return {
      mode,
      retailPrice,
      basePrice,
      proPrice,
      rebatePercent: null,
      rebateAmount: null,
      hasSavings: savingsAmount > 0,
      savingsAmount,
    };
  }
  
  // Rebate mode
  if (mode === 'rebate') {
    let percent = product.pro_rebate_percent ?? 10;
    // Clamp between 0 and 50
    percent = Math.min(Math.max(percent, 0), 50);
    
    if (percent === 0) {
      return defaultResult;
    }
    
    const rebateAmount = Math.round(basePrice * (percent / 100));
    
    return {
      mode,
      retailPrice,
      basePrice,
      proPrice: null,
      rebatePercent: percent,
      rebateAmount,
      hasSavings: rebateAmount > 0,
      savingsAmount: rebateAmount,
    };
  }
  
  // Fallback for unknown modes
  return defaultResult;
}

/**
 * Check if a product has an active sale
 */
export function isSaleActive(product: ProductPricing): boolean {
  if (!product.sale_enabled || !product.sale_price || !product.sale_ends_at) {
    return false;
  }
  
  const now = new Date();
  const start = product.sale_starts_at ? new Date(product.sale_starts_at) : new Date(0);
  const end = new Date(product.sale_ends_at);
  
  return now >= start && now <= end;
}
